import {LLMRole} from 'graphql-client/__generated/graphqlGlobalTypes';
import {
  AskUserAnswer,
  AskUserDraft,
  AskUserQuestion,
  AskUserQuestionType,
  AskUserReply,
  AskUserResponse,
} from 'llm/chat-library/ask-user/AskUserTypes';
import {ClientDisplayChatEvent, DisplayMessageChatEventNode} from 'llm/chat-library/messages/MessageTypes';
import {assertNever} from 'util/Types';

/** Keep in sync with MAX_ANSWER_LENGTH in benchling/llm/tools/ask_user_tool.py. */
export const MAX_ANSWER_LENGTH = 2000;

/** The AskUser questions an assistant message asks, or null if it asks none. */
export function getAskUserRequest(
  event: DisplayMessageChatEventNode
): {questions: Array<AskUserQuestion>} | null {
  const request = event.askUserRequest;
  if (request == null) {
    return null;
  }
  // The AskUser tool validates the questions before they are stored.
  return {questions: request.questions as Array<AskUserQuestion>};
}

/**
 * The user's reply to the questions on the assistant message at `index` in `events`, or null while
 * they are open or when the message asks no questions.
 */
export function getAskUserReply(events: ReadonlyArray<ClientDisplayChatEvent>, index: number): AskUserReply {
  const assistantEvent = events[index];
  if (assistantEvent.__typename !== 'DisplayMessageChatEventNode' || assistantEvent.askUserRequest == null) {
    return null;
  }
  for (let i = index + 1; i < events.length; i++) {
    const event = events[i];
    if (event.role !== LLMRole.USER) {
      continue;
    }
    if (
      event.__typename !== 'DisplayMessageChatEventNode' ||
      event.askUserResponse == null ||
      event.askUserResponse.requestEvent.id !== assistantEvent.id
    ) {
      return {response: null};
    }
    const {status, answers} = event.askUserResponse;
    // The server validates answers against the questions before they are stored.
    return {response: {status, answers: answers as Record<string, AskUserAnswer>}};
  }
  return null;
}

/** The answer to a question, or undefined when it was skipped. */
export function getAskUserAnswer(response: AskUserResponse, questionId: string): AskUserAnswer | undefined {
  // Question ids such as "constructor" would otherwise read inherited Object properties.
  return Object.hasOwn(response.answers, questionId) ? response.answers[questionId] : undefined;
}

export function createEmptyAskUserDraft(): AskUserDraft {
  return {selectedLabels: [], isOtherSelected: false, otherText: '', text: ''};
}

/** Whether the user has not started answering, so the card should not show a validation message. */
export function isAskUserDraftEmpty(question: AskUserQuestion, draft: AskUserDraft): boolean {
  if (question.type === AskUserQuestionType.FREE_TEXT) {
    return draft.text.trim().length === 0;
  }
  return draft.selectedLabels.length === 0 && !draft.isOtherSelected;
}

/** Return a message describing why the draft cannot be submitted, or null when it is valid. */
export function getAskUserDraftError(question: AskUserQuestion, draft: AskUserDraft): string | null {
  if (question.type === AskUserQuestionType.FREE_TEXT) {
    const text = draft.text.trim();
    if (text.length === 0) {
      return 'Enter an answer.';
    }
    if (text.length > MAX_ANSWER_LENGTH) {
      return `Use at most ${MAX_ANSWER_LENGTH} characters.`;
    }
    return null;
  }
  if (draft.isOtherSelected) {
    const otherText = draft.otherText.trim();
    if (otherText.length === 0) {
      return 'Describe your answer.';
    }
    if (otherText.length > MAX_ANSWER_LENGTH) {
      return `Use at most ${MAX_ANSWER_LENGTH} characters.`;
    }
  }
  const selectionCount = draft.selectedLabels.length + (draft.isOtherSelected ? 1 : 0);
  if (question.type === AskUserQuestionType.SINGLE_SELECT) {
    return selectionCount === 1 ? null : 'Choose an option.';
  }
  return selectionCount > 0 ? null : 'Choose at least one option.';
}

/** Build the answer for a valid draft, in the shape the server expects. */
export function buildAskUserAnswer(question: AskUserQuestion, draft: AskUserDraft): AskUserAnswer {
  switch (question.type) {
    case AskUserQuestionType.FREE_TEXT:
      return {type: AskUserQuestionType.FREE_TEXT, text: draft.text.trim()};
    case AskUserQuestionType.SINGLE_SELECT:
      return {
        type: AskUserQuestionType.SINGLE_SELECT,
        selected: draft.isOtherSelected ? draft.otherText.trim() : draft.selectedLabels[0],
      };
    case AskUserQuestionType.MULTI_SELECT: {
      const selected = question.options
        .map((option) => option.label)
        .filter((label) => draft.selectedLabels.includes(label));
      const otherText = draft.otherText.trim();
      return {
        type: AskUserQuestionType.MULTI_SELECT,
        selected:
          draft.isOtherSelected && !selected.includes(otherText) ? [...selected, otherText] : selected,
      };
    }
    default:
      assertNever(question);
      return {type: AskUserQuestionType.FREE_TEXT, text: draft.text.trim()};
  }
}

export function formatAskUserAnswer(answer: AskUserAnswer): string {
  switch (answer.type) {
    case AskUserQuestionType.SINGLE_SELECT:
      return answer.selected;
    case AskUserQuestionType.MULTI_SELECT:
      return answer.selected.join(', ');
    case AskUserQuestionType.FREE_TEXT:
      return answer.text;
    default:
      assertNever(answer);
      return '';
  }
}

/** The text of the chat message that carries a response, e.g. "Organism: E. coli · Assay: ELISA". */
export function formatAskUserResponseMessage(
  questions: ReadonlyArray<AskUserQuestion>,
  response: AskUserResponse
): string {
  const answers = questions.flatMap((question) => {
    const answer = getAskUserAnswer(response, question.id);
    return answer != null ? [`${question.header}: ${formatAskUserAnswer(answer)}`] : [];
  });
  return answers.length > 0 ? answers.join(' · ') : 'Skipped the questions.';
}
